import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import ShareButtons from '../components/ShareButtons';
import { 
  Calendar,
  ArrowLeft,
  MessageSquare,
  Send,
  User,
  BookOpen,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArticleDetailPage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({ author_name: '', content: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [translatedTitle, setTranslatedTitle] = useState('');
  const [translatedContent, setTranslatedContent] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const { language, translateContent } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articleRes, commentsRes] = await Promise.all([
          axios.get(`${API_URL}/api/articles/${id}`),
          axios.get(`${API_URL}/api/articles/${id}/comments`)
        ]);
        setArticle(articleRes.data);
        setComments(commentsRes.data || []);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Translate article when language changes
  useEffect(() => {
    const translateArticle = async () => {
      if (!article) return;
      
      if (language === 'ro') {
        setTranslatedTitle(article.title);
        setTranslatedContent(article.content);
        return;
      }

      setIsTranslating(true);
      try {
        const [title, content] = await Promise.all([
          translateContent(article.title, 'article_title', `${article.id}_title`),
          translateContent(article.content, 'article_content', `${article.id}_content`)
        ]);
        setTranslatedTitle(title);
        setTranslatedContent(content);
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedTitle(article.title);
        setTranslatedContent(article.content);
      } finally {
        setIsTranslating(false);
      }
    };

    translateArticle();
  }, [article, language, translateContent]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.author_name.trim() || !commentForm.content.trim()) return;

    setSubmitting(true);
    setMessage(null);

    try {
      const response = await axios.post(`${API_URL}/api/articles/${id}/comments`, commentForm);
      setComments([response.data, ...comments]);
      setCommentForm({ author_name: '', content: '' });
      setMessage({ type: 'success', text: language === 'ro' ? 'Comentariu adăugat!' : 'Comment added!' });
    } catch (error) {
      setMessage({ type: 'error', text: language === 'ro' ? 'Eroare la adăugarea comentariului' : 'Error adding comment' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'ro' ? 'ro-RO' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Translations
  const t = {
    backToJournal: language === 'ro' ? 'Înapoi la Jurnal' : language === 'de' ? 'Zurück zum Tagebuch' : 'Back to Journal',
    comments: language === 'ro' ? 'Comentarii' : language === 'de' ? 'Kommentare' : 'Comments',
    leaveComment: language === 'ro' ? 'Lasă un comentariu' : language === 'de' ? 'Hinterlasse einen Kommentar' : 'Leave a comment',
    yourName: language === 'ro' ? 'Numele tău' : language === 'de' ? 'Dein Name' : 'Your name',
    yourComment: language === 'ro' ? 'Comentariul tău...' : language === 'de' ? 'Dein Kommentar...' : 'Your comment...',
    send: language === 'ro' ? 'Trimite' : language === 'de' ? 'Senden' : 'Send',
    noComments: language === 'ro' ? 'Fii primul care comentează!' : language === 'de' ? 'Sei der Erste, der kommentiert!' : 'Be the first to comment!',
    notFound: language === 'ro' ? 'Articolul nu a fost găsit' : language === 'de' ? 'Artikel nicht gefunden' : 'Article not found',
    translating: language === 'ro' ? 'Se traduce...' : language === 'de' ? 'Übersetzen...' : 'Translating...'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">{t.notFound}</p>
          <Link to="/jurnal" className="text-sky-600 hover:underline mt-4 inline-block">
            {t.backToJournal}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="article-detail-page">
      {/* Back Link */}
      <div className="bg-slate-50 border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link 
            to="/jurnal" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backToJournal}
          </Link>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Calendar className="w-4 h-4" />
            {formatDate(article.created_at)}
          </div>
          {isTranslating ? (
            <div className="flex items-center gap-2 mb-6">
              <div className="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-slate-400">{t.translating}</span>
            </div>
          ) : (
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              {translatedTitle || article.title}
            </h1>
          )}
        </header>

        {/* Featured Image */}
        {article.image_url && (
          <div className="mb-8 rounded-2xl overflow-hidden">
            <img 
              src={article.image_url} 
              alt={translatedTitle || article.title}
              className="w-full h-auto"
            />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none mb-12">
          {isTranslating ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-slate-400">{t.translating}</span>
            </div>
          ) : (
            (translatedContent || article.content).split('\n').map((paragraph, idx) => (
              paragraph.trim() && (
                <p key={idx} className="text-slate-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              )
            ))
          )}
        </div>

        {/* Share */}
        <div className="border-t border-b py-6 mb-12">
          <ShareButtons 
            title={translatedTitle || article.title}
            description={(translatedContent || article.content).substring(0, 150)}
          />
        </div>

        {/* Comments Section */}
        <section className="space-y-8">
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-sky-500" />
            {t.comments} ({comments.length})
          </h2>

          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} className="bg-slate-50 rounded-2xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">{t.leaveComment}</h3>
            
            {message && (
              <div className={`p-3 rounded-xl mb-4 text-sm ${
                message.type === 'success' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {message.text}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  value={commentForm.author_name}
                  onChange={(e) => setCommentForm({...commentForm, author_name: e.target.value})}
                  placeholder={t.yourName}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
              <div>
                <textarea
                  value={commentForm.content}
                  onChange={(e) => setCommentForm({...commentForm, content: e.target.value})}
                  placeholder={t.yourComment}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
                />
              </div>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? '...' : t.send}
              </Button>
            </div>
          </form>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-slate-500 text-center py-8">{t.noComments}</p>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className="bg-white border rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{comment.author_name}</p>
                      <p className="text-xs text-slate-500">{formatDate(comment.created_at)}</p>
                    </div>
                  </div>
                  <p className="text-slate-700">{comment.content}</p>
                </div>
              ))
            )}
          </div>
        </section>
      </article>
    </div>
  );
};

export default ArticleDetailPage;
